import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  Search,
  Film,
  Music,
  AlertCircle,
  CheckCircle2,
  X,
  Image as ImageIcon,
  Video,
} from "lucide-react";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Input } from "@/components/ui/Input.tsx";
import { Select } from "@/components/ui/Select.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { adminApi } from "@/api/admin.ts";

const PAGE_SIZE = 25;

const STATUS_VARIANTS: Record<string, "default" | "success" | "danger" | "warning" | "info" | "muted"> = {
  complete: "success",
  failed: "danger",
  previews_failed: "danger",
  publish_failed: "danger",
  draft: "muted",
  rendering: "info",
  generating_clips: "info",
};

const ALL_STATUSES = [
  "draft", "script_generating", "script_ready", "script_approved",
  "storyboard_generating", "storyboard_ready", "storyboard_approved",
  "voiceover_generating", "generating_clips", "clips_ready",
  "rendering", "complete", "failed", "previews_failed", "publish_failed",
];

export function AdminVideosPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);

  // ── List query ────────────────────────────────────────────────────────
  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ["admin", "videos", { search, status: statusFilter, offset }],
    queryFn: () =>
      adminApi.listVideos({
        limit: PAGE_SIZE,
        offset,
        status: statusFilter || undefined,
        search: search || undefined,
      }),
  });

  const videos = listData?.videos ?? [];
  const total = listData?.pagination?.total ?? 0;

  // ── Detail query ──────────────────────────────────────────────────────
  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ["admin", "video", selectedVideoId],
    queryFn: () => adminApi.getVideo(selectedVideoId!),
    enabled: !!selectedVideoId,
  });

  const formatDate = (d: string) =>
    new Date(d).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const truncate = (s: string | null | undefined, n: number) =>
    s && s.length > n ? s.slice(0, n) + "…" : s ?? "—";

  // ── Detail Panel ──────────────────────────────────────────────────────
  const DetailPanel = () => {
    if (!detailData) return null;
    const { video: v, segments: segs, providerAttempts: attempts } = detailData;
    return (
      <div
        className="fixed inset-0 bg-black/50 z-50 flex justify-end"
        onClick={() => setSelectedVideoId(null)}
      >
        <div
          className="bg-white w-full max-w-3xl h-full overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-lg font-semibold">Video Detail</h2>
              <p className="text-xs text-gray-400 font-mono">{v.id}</p>
            </div>
            <Button variant="ghost" size="sm" aria-label="Close" onClick={() => setSelectedVideoId(null)}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="p-6 space-y-6">
            {/* Meta grid */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-xs text-gray-400 uppercase block">Status</span>
                <Badge variant={STATUS_VARIANTS[v.status] ?? "default"}>{v.status}</Badge>
              </div>
              <div>
                <span className="text-xs text-gray-400 uppercase block">User</span>
                {v.userEmail ?? v.userId}
              </div>
              <div>
                <span className="text-xs text-gray-400 uppercase block">Format</span>
                {v.format}
              </div>
              <div>
                <span className="text-xs text-gray-400 uppercase block">Credits</span>
                {v.creditsUsed}
              </div>
              <div>
                <span className="text-xs text-gray-400 uppercase block">Created</span>
                {formatDate(v.createdAt)}
              </div>
              <div>
                <span className="text-xs text-gray-400 uppercase block">Updated</span>
                {formatDate(v.updatedAt)}
              </div>
            </div>

            {/* Error */}
            {v.errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{v.errorMessage}</p>
              </div>
            )}

            {/* Final Video */}
            {v.finalVideoUrl && (
              <div>
                <span className="text-xs text-gray-400 uppercase block mb-2">Final Video</span>
                <div className="bg-black rounded-lg overflow-hidden">
                  <video src={v.finalVideoUrl} controls className="w-full max-h-[400px]" />
                </div>
                <a
                  href={v.finalVideoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                >
                  <Download className="w-3.5 h-3.5" /> Download video
                </a>
              </div>
            )}

            {/* Voiceover */}
            {v.voiceoverUrl && (
              <div>
                <span className="text-xs text-gray-400 uppercase block mb-2">Voiceover</span>
                <audio src={v.voiceoverUrl} controls className="w-full" />
                <a
                  href={v.voiceoverUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                >
                  <Download className="w-3.5 h-3.5" /> Download audio
                </a>
              </div>
            )}

            {/* Script */}
            {v.scriptText && (
              <div>
                <span className="text-xs text-gray-400 uppercase block mb-2">Script</span>
                <div className="bg-gray-50 rounded-lg p-3 text-sm whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                  {v.scriptText}
                </div>
              </div>
            )}

            {/* Segments */}
            <div>
              <span className="text-xs text-gray-400 uppercase block mb-2">
                Segments ({segs.length})
              </span>
              <div className="space-y-3">
                {segs.map((seg) => (
                  <div key={seg.id} className="border rounded-lg p-3">
                    <div className="flex gap-3">
                      {seg.imageUrl ? (
                        <a href={seg.imageUrl} target="_blank" rel="noopener noreferrer">
                          <img
                            src={seg.imageUrl}
                            alt={`Segment ${seg.orderIndex + 1}`}
                            className="w-20 h-36 object-cover rounded flex-shrink-0"
                          />
                        </a>
                      ) : (
                        <div className="w-20 h-36 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                          <ImageIcon className="w-6 h-6 text-gray-300" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-gray-500">
                            Frame {seg.orderIndex + 1}
                          </span>
                          {seg.imageModel && (
                            <Badge variant="muted" className="text-[10px] px-1 py-0">
                              {seg.imageModel}
                            </Badge>
                          )}
                          {seg.durationMs != null && (
                            <span className="text-xs text-gray-400">
                              {(seg.durationMs / 1000).toFixed(1)}s
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 line-clamp-2">{seg.text}</p>
                        <div className="flex gap-3 mt-2">
                          {seg.imageUrl && (
                            <a
                              href={seg.imageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline inline-flex items-center gap-0.5"
                            >
                              <ImageIcon className="w-3 h-3" /> Image
                            </a>
                          )}
                          {seg.videoClipUrl && (
                            <a
                              href={seg.videoClipUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline inline-flex items-center gap-0.5"
                            >
                              <Film className="w-3 h-3" /> Final clip
                            </a>
                          )}
                          {seg.videoClipPreviewUrl && (
                            <a
                              href={seg.videoClipPreviewUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline inline-flex items-center gap-0.5"
                            >
                              <Film className="w-3 h-3" /> Preview
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Provider Attempts */}
            {attempts.length > 0 && (
              <div>
                <span className="text-xs text-gray-400 uppercase block mb-2">
                  Provider Attempts ({attempts.length})
                </span>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-1.5 pr-2">Provider</th>
                        <th className="py-1.5 pr-2">Kind</th>
                        <th className="py-1.5 pr-2">Model</th>
                        <th className="py-1.5 pr-2">Result</th>
                        <th className="py-1.5 pr-2">Latency</th>
                        <th className="py-1.5">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attempts.map((a) => (
                        <tr key={a.id} className="border-b last:border-0">
                          <td className="py-1.5 pr-2 font-medium">{a.moduleId}</td>
                          <td className="py-1.5 pr-2">
                            {a.kind}
                            {a.tier ? `/${a.tier}` : ""}
                          </td>
                          <td className="py-1.5 pr-2 text-gray-500">{a.modelSlug ?? "—"}</td>
                          <td className="py-1.5 pr-2">
                            {a.success ? (
                              <span className="text-green-600 inline-flex items-center gap-0.5">
                                <CheckCircle2 className="w-3 h-3" /> OK
                              </span>
                            ) : (
                              <span className="text-red-600">{a.category ?? "error"}</span>
                            )}
                          </td>
                          <td className="py-1.5 pr-2 text-gray-500">
                            {a.latencyMs != null ? `${(a.latencyMs / 1000).toFixed(1)}s` : "—"}
                          </td>
                          <td className="py-1.5 text-gray-500 max-w-[200px] truncate" title={a.errorMessage ?? undefined}>
                            {a.errorMessage ?? "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Main ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Videos</h1>
        <span className="text-sm text-gray-500">{total} total</span>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input
            placeholder="Search scripts..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOffset(0);
            }}
            className="pl-9"
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setOffset(0);
          }}
          options={[
            { value: "", label: "All statuses" },
            ...ALL_STATUSES.map((s) => ({ value: s, label: s })),
          ]}
        />
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-gray-500">
              <th className="px-4 py-3 font-medium">Video</th>
              <th className="px-4 py-3 font-medium">User</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Format</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 font-medium">Assets</th>
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            {listLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                  <Spinner className="mx-auto mb-2" />
                  Loading…
                </td>
              </tr>
            ) : videos.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                  No videos found
                </td>
              </tr>
            ) : (
              videos.map((v) => (
                <tr
                  key={v.id}
                  className="border-t hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedVideoId(v.id)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {v.selectedThumbnailUrl ? (
                        <img
                          src={v.selectedThumbnailUrl}
                          className="w-10 h-10 rounded object-cover"
                          alt=""
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center">
                          <Video className="w-4 h-4 text-gray-300" />
                        </div>
                      )}
                      <span className="text-gray-700 truncate max-w-[200px]">
                        {truncate(v.scriptPreview, 60)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {v.userEmail ?? v.userId.slice(0, 8)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={STATUS_VARIANTS[v.status] ?? "default"} className="text-xs">
                      {v.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{v.format}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(v.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      {v.finalVideoUrl && (
                        <a
                          href={v.finalVideoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="p-1 hover:bg-blue-50 rounded text-blue-600"
                          title="Download video"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      )}
                      {v.voiceoverUrl && (
                        <a
                          href={v.voiceoverUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="p-1 hover:bg-purple-50 rounded text-purple-600"
                          title="Download voiceover"
                        >
                          <Music className="w-4 h-4" />
                        </a>
                      )}
                      {v.errorMessage && (
                        <span
                          className="p-1 text-red-400"
                          title={v.errorMessage}
                        >
                          <AlertCircle className="w-4 h-4" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedVideoId(v.id);
                      }}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>
          Showing {total > 0 ? offset + 1 : 0}–{Math.min(offset + PAGE_SIZE, total)} of {total}
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            aria-label="Previous page"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            aria-label="Next page"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={offset + PAGE_SIZE >= total}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Detail panel */}
      {selectedVideoId &&
        (detailLoading ? (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <Spinner className="w-8 h-8 text-white" />
          </div>
        ) : detailData ? (
          <DetailPanel />
        ) : null)}
    </div>
  );
}
